import { useRef, useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import Header from "../components/Header";
import Contact from "../components/Contact";
import Footer from "../components/Footer";
import { ButtonPrimary, ButtonOutline } from "../components/Button";
import { getProjectBySlug } from "../data/projects";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const MEDIA_ITEM_HEIGHT = 200;
const MEDIA_GAP = 16;
const VISIBLE_MEDIA_COUNT = 2;
const GALLERY_HEIGHT =
  MEDIA_ITEM_HEIGHT * VISIBLE_MEDIA_COUNT +
  MEDIA_GAP * (VISIBLE_MEDIA_COUNT - 1);

const VIDEO_EXT = /\.(webm|mp4|ogg|mov)$/i;

const normalizeMedia = (item) => {
  if (typeof item === "string") {
    return VIDEO_EXT.test(item)
      ? { type: "video", src: item }
      : { type: "image", src: item };
  }
  return item;
};

function ProjectMedia({ media, title, index, onMediaLoad }) {
  const { type, src, poster } = normalizeMedia(media);

  return (
    <figure
      data-media-item
      className="img-box h-[200px] rounded-xl overflow-hidden ring-1 ring-inset ring-zinc-50/10 shrink-0 bg-zinc-800"
    >
      {type === "video" ? (
        <video
          src={src}
          poster={poster}
          controls
          playsInline
          preload="metadata"
          className="w-full h-full object-cover object-top"
          aria-label={`${title} demo video`}
        />
      ) : (
        <img
          src={src}
          alt={`${title} screenshot ${index + 1}`}
          loading={index === 0 ? "eager" : "lazy"}
          className="w-full h-full object-cover object-top"
          onLoad={onMediaLoad}
        />
      )}
    </figure>
  );
}

function ProjectMediaGallery({ screenshots, title }) {
  const scrollRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 4);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [screenshots, updateScrollState]);

  const scrollByStep = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = MEDIA_ITEM_HEIGHT + MEDIA_GAP;
    el.scrollBy({ top: direction * step, behavior: "smooth" });
  };

  const showArrows = screenshots.length > VISIBLE_MEDIA_COUNT;

  return (
    <div className="flex gap-2 min-h-0">
      <div
        ref={scrollRef}
        className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden space-y-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ maxHeight: GALLERY_HEIGHT }}
        aria-label="Project media"
      >
        {screenshots.map((media, index) => (
          <ProjectMedia
            key={`${normalizeMedia(media).src}-${index}`}
            media={media}
            title={title}
            index={index}
            onMediaLoad={updateScrollState}
          />
        ))}
      </div>

      {showArrows && (
        <div className="flex flex-col justify-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => scrollByStep(-1)}
            disabled={!canScrollUp}
            aria-label="Show previous screenshots"
            className="w-10 h-10 grid place-items-center rounded-xl bg-zinc-50/10 ring-1 ring-inset ring-zinc-50/10 text-zinc-50 transition-colors hover:bg-zinc-50/15 disabled:opacity-30 disabled:pointer-events-none"
          >
            <span className="material-symbols-rounded">keyboard_arrow_up</span>
          </button>
          <button
            type="button"
            onClick={() => scrollByStep(1)}
            disabled={!canScrollDown}
            aria-label="Show next screenshots"
            className="w-10 h-10 grid place-items-center rounded-xl bg-zinc-50/10 ring-1 ring-inset ring-zinc-50/10 text-zinc-50 transition-colors hover:bg-zinc-50/15 disabled:opacity-30 disabled:pointer-events-none"
          >
            <span className="material-symbols-rounded">
              keyboard_arrow_down
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

function ProjectDetail() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  useGSAP(() => {
    const elements = gsap.utils.toArray(".reveal-up");

    elements.forEach((element) => {
      gsap.to(element, {
        scrollTrigger: {
          trigger: element,
          start: "-200 bottom",
          end: "bottom 80%",
          scrub: true,
        },
        y: 0,
        opacity: 1,
        duration: 1,
        ease: "power2.out",
      });
    });
  });

  if (!project) {
    return (
      <ReactLenis root>
        <Header />
        <main>
          <section className="section pt-28">
            <div className="container text-center">
              <h1 className="headline-2 mb-4">Project not found</h1>
              <Link to="/#projects" className="btn btn-outline">
                Back to projects
              </Link>
            </div>
          </section>
          <Contact />
        </main>
        <Footer />
      </ReactLenis>
    );
  }

  const {
    title,
    tags,
    description,
    features,
    screenshots,
    websiteLink,
    githubLink,
  } = project;

  return (
    <ReactLenis root>
      <Header />
      <main>
        <section className="section pt-28 pb-10">
        <div className="container">
          <Link
            to="/#projects"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-50 transition-colors"
          >
            <span className="material-symbols-rounded text-[18px]">
              arrow_back
            </span>
            Back to projects
          </Link>

          <div className="mt-8">
            <h1 className="headline-2 mb-4">{title}</h1>

            <div className="flex flex-wrap gap-2 mb-8">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="h-8 text-sm text-zinc-400 bg-zinc-50/5 grid items-center px-3 rounded-lg"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start">
              <div>
                <p className="text-zinc-400 leading-relaxed mb-8">
                  {description}
                </p>

                <h2 className="text-lg font-semibold text-zinc-100 mb-4">
                  Features
                </h2>
                <ul className="space-y-3">
                  {features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-3 text-zinc-300"
                    >
                      <span className="material-symbols-rounded text-sky-400 text-[20px] mt-0.5 shrink-0">
                        check_circle
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 lg:mt-0">
                <ProjectMediaGallery screenshots={screenshots} title={title} />
              </div>
            </div>

            {(websiteLink || githubLink) && (
              <div className="flex flex-wrap items-center gap-3 mt-10 pt-8 border-t border-zinc-50/10">
                {websiteLink && (
                  <ButtonPrimary
                    label="Visit the website"
                    icon="open_in_new"
                    href={websiteLink}
                    target="_blank"
                  />
                )}
                {githubLink && (
                  <ButtonOutline
                    label="See on GitHub"
                    icon="code"
                    href={githubLink}
                    target="_blank"
                  />
                )}
              </div>
            )}
          </div>
        </div>
        </section>
        <Contact />
      </main>
      <Footer />
    </ReactLenis>
  );
}

export default ProjectDetail;
